"use client"

import { useEffect, useState } from "react"
import type { Animal } from "@/lib/types"
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts"

export function AnimalGrowthChart({ animal }: { animal: Animal }) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  // Generate mock growth data based on birth weight and current weight
  const generateGrowthData = () => {
    const birthWeight = animal.birthWeight
    const currentWeight = animal.weight
    const birthDate = new Date(animal.birthDate)
    const today = new Date()
    const ageInDays = Math.floor((today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24))

    // Create 6 data points from birth to current
    const data = []
    const interval = Math.max(1, Math.floor(ageInDays / 5))

    for (let i = 0; i <= 5; i++) {
      const daysSinceBirth = i * interval
      const date = new Date(birthDate)
      date.setDate(date.getDate() + daysSinceBirth)

      // Calculate weight with some randomness for realistic growth curve
      const progress = i / 5
      const baseWeight = birthWeight + (currentWeight - birthWeight) * progress
      const randomFactor = 0.95 + Math.random() * 0.1 // 0.95 to 1.05
      const weight = i === 5 ? currentWeight : baseWeight * randomFactor

      // Calculate ADG
      const adg = i === 0 ? 0 : ((weight - birthWeight) / daysSinceBirth) * 1000

      data.push({
        day: daysSinceBirth,
        date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        weight: Number.parseFloat(weight.toFixed(1)),
        adg: Number.parseFloat(adg.toFixed(1)),
      })
    }

    return data
  }

  const growthData = generateGrowthData()

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={growthData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1} />
          </linearGradient>
          <linearGradient id="colorADG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
        <XAxis dataKey="date" tick={{ fill: "var(--foreground)", fontSize: 12 }} />
        <YAxis
          yAxisId="left"
          tick={{ fill: "var(--foreground)", fontSize: 12 }}
          label={{
            value: "Weight (kg)",
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle", fill: "var(--foreground)" },
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fill: "var(--foreground)", fontSize: 12 }}
          label={{
            value: "ADG (g/day)",
            angle: 90,
            position: "insideRight",
            style: { textAnchor: "middle", fill: "var(--foreground)" },
          }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        />
        <Legend />
        <Area
          type="monotone"
          dataKey="weight"
          yAxisId="left"
          stroke="#8884d8"
          fillOpacity={1}
          fill="url(#colorWeight)"
          animationDuration={1000}
          animationEasing="ease-in-out"
        />
        <Area
          type="monotone"
          dataKey="adg"
          yAxisId="right"
          stroke="#82ca9d"
          fillOpacity={1}
          fill="url(#colorADG)"
          animationDuration={1000}
          animationEasing="ease-in-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
